/**
 * Central theme configuration for the wedding template.
 *
 * Edit this single file to change the entire color palette and typography
 * of the invitation. Every section reads its colors and fonts from here
 * (through the ThemeProvider, which maps these values to CSS variables).
 *
 * Colors use the OKLCH color space, written as a triplet:
 *   [Lightness, Chroma, Hue]
 *   - Lightness: 0 (black) to 1 (white)
 *   - Chroma:    0 (neutral) to ~0.4 (very saturated)
 *   - Hue:       degrees on the color wheel (0-360)
 */

export type OklchColor = [number, number, number];

export interface WeddingFonts {
  /** Serif used for titles and names (e.g. Cormorant Garamond). */
  title: string;
  /** Sans-serif used for body text (e.g. Josefin Sans). */
  body: string;
  /** Calligraphic / script font used for the couple names (e.g. Great Vibes). */
  accent: string;
}

export interface WeddingColors {
  /** Page background (warm ivory / uncoated paper). */
  background: OklchColor;
  /** Alternate section background (subtle warm beige). */
  backgroundAlt: OklchColor;
  /** Main text color (deep espresso brown). */
  text: OklchColor;
  /** Accent color (antique champagne) — decorative lines, icons, highlights. */
  accent: OklchColor;
  /** Muted / secondary text color (taupe). */
  muted: OklchColor;
  /** Deep espresso-black used for the opening hero and closing photo. */
  dark: OklchColor;
  /** Background color for primary buttons (defaults to the accent). */
  buttonBackground: OklchColor;
  /** Text color for primary buttons (ivory by default). */
  buttonText: OklchColor;
}

export interface WeddingTheme {
  fonts: WeddingFonts;
  colors: WeddingColors;
}

export const weddingTheme: WeddingTheme = {
  fonts: {
    title: "'Cormorant Garamond', 'Playfair Display', serif",
    body: "'Josefin Sans', 'DM Sans', sans-serif",
    accent: "'Great Vibes', cursive",
  },
  colors: {
    background: [0.975, 0.006, 78],
    backgroundAlt: [0.945, 0.012, 72],
    text: [0.16, 0.018, 32],
    accent: [0.68, 0.1, 62],
    muted: [0.48, 0.035, 38],
    dark: [0.15, 0.015, 28],
    buttonBackground: [0.68, 0.1, 62],
    buttonText: [0.975, 0.006, 78],
  },
};